import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LuBell, LuBookOpen, LuCheck, LuMail, LuUsers } from 'react-icons/lu';
import { useNotificationsOptional } from '../../context/NotificationContext';
import './notifications.css';

function formatWhen(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = Date.now();
    const diff = now - d.getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

function TypeIcon({ type }) {
  const t = (type || '').toUpperCase();
  if (t.includes('TEAM') || t.includes('WORKSPACE')) return <LuUsers aria-hidden />;
  if (t.includes('NOTEBOOK') || t.includes('SHARE')) return <LuMail aria-hidden />;
  if (t.includes('LEARN') || t.includes('IMPORT')) return <LuBookOpen aria-hidden />;
  return <LuBell aria-hidden />;
}

export default function NotificationBell() {
  const nav = useNotificationsOptional();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!nav) return null;

  const { unreadCount, items, loading, refresh, markRead, markAllRead } = nav;

  const openItem = async (item) => {
    if (!item.read && item.id) {
      try {
        await markRead(item.id);
      } catch {
        /* ignore */
      }
    }
    setOpen(false);
    if (item.linkPath) navigate(item.linkPath);
  };

  return (
    <div className="app-nav-notifications" ref={rootRef}>
      <button
        type="button"
        className="app-nav-notifications-btn"
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        onClick={() => {
          setOpen((v) => !v);
          if (!open) refresh();
        }}
      >
        <LuBell className="app-nav-notifications-icon" aria-hidden />
        {unreadCount > 0 && (
          <span className="app-nav-notifications-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="app-notifications-panel" role="dialog" aria-label="Notifications">
          <header className="app-notifications-head">
            <h2 className="app-notifications-title">Notifications</h2>
            <div className="app-notifications-head-actions">
              {unreadCount > 0 && (
                <button type="button" className="app-notifications-mark-all" onClick={markAllRead}>
                  <LuCheck aria-hidden />
                  Mark all read
                </button>
              )}
            </div>
          </header>

          <div className="app-notifications-list">
            {loading && items.length === 0 && (
              <p className="app-notifications-empty">Loading…</p>
            )}
            {!loading && items.length === 0 && (
              <p className="app-notifications-empty">You&apos;re all caught up.</p>
            )}
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`app-notifications-item${item.read ? '' : ' is-unread'}`}
                onClick={() => openItem(item)}
              >
                <span className="app-notifications-item-icon">
                  <TypeIcon type={item.type} />
                </span>
                <span className="app-notifications-item-body">
                  <span className="app-notifications-item-title">{item.title}</span>
                  {item.body && (
                    <span className="app-notifications-item-text">{item.body}</span>
                  )}
                  <span className="app-notifications-item-time">{formatWhen(item.createdAt)}</span>
                </span>
              </button>
            ))}
          </div>

          <footer className="app-notifications-foot">
            <Link to="/notifications" onClick={() => setOpen(false)}>
              View all
            </Link>
            <Link to="/settings" onClick={() => setOpen(false)}>
              Preferences
            </Link>
          </footer>
        </div>
      )}
    </div>
  );
}
